import React from 'react';
import ChangeAllReviewModal from './ChangeAllReviewModal';
import ordersService, {
  type ChangeAllChange,
  type ChangeAllChangeSet,
  type ChangeAllPreviewRow,
  type ChangeAllApplyResult,
  type OrderDetails,
} from '../../../services/orders.service';
import { invoiceLineItemsService } from '../../../services/invoiceLineItems.service';
import { toBackendDateValue } from '../../../utils/buildOrderDataRows';

/**
 * Change All — the whole fan-out for a pro: what the user flagged anywhere in the drawer,
 * assembled into ONE Apply.
 *
 * The Invoicing tab's flags used to fan separately, because the tab was built later with
 * its own Save and kept its own flag set. That split was an artifact, not a requirement:
 * a ChangeAllChangeSet already carries `order`, `invoice` AND `legs`, and the backend's
 * apply_change_all writes all three in a single transaction per pro. Two Applies meant
 * two rule-engine runs over the same pro, which V1's one-pass `doupdateall` never did —
 * so a rule reacting to two fields together, or an EDI effect that must fire once, could
 * come out differently. They are one ChangeSet now: one preview, one nonce, one saverules
 * run per pro (bugfix #33440).
 *
 * Invoice LINE ITEMS remain a second endpoint inside that one Apply — they aren't fields,
 * they don't run saverules, and the line-item service owns their matching rules. The
 * field arm applies FIRST (saverules there can PRODUCE line items), then the line-item
 * arm on top, so a user's line edits land last. ACCESSORIAL invoices are their own
 * records with their own bulk surface and are deliberately outside this scope.
 *
 * ProDetailContainer owns all of it. Nothing here may live in a tab: the tabs unmount,
 * and a fan-out driven from one silently did nothing when the user saved from another.
 */

// ── Change All: bulk fan-out of the editable invoicing header fields ──────────
// Maps the currently-checked header fields to a ChangeAllChangeSet so the
// standard change-all preview/apply endpoints fan them out to the rest of the
// target set (fired after the footer Save commits the anchor). Each field routes
// to the INVOICE arm (main invoice row) or the ORDER arm (ordersall). Empty
// buckets are omitted so the review modal only lists real edits.
export type InvArm = 'invoice' | 'order';

// Simple 1:1 fields: view key -> { DB column, arm }. `numeric` sends a number
// (empty -> null) — `zeroDefault` sends 0 instead of null for a NOT-NULL int col.
// `datetime` normalizes the header's display string ('MM/DD/YYYY HH:MM') to backend
// 'YYYY-MM-DD HH:MM:SS' via toBackendDateValue — fanning the raw display string made
// MySQL coerce it to 0000-00-00 on every target pro.
export const INVOICE_SIMPLE_FIELDS: Record<string, { column: string; arm: InvArm; numeric?: boolean; zeroDefault?: boolean; datetime?: boolean }> = {
  // invoice-table
  invoiceComments: { column: 'invoicecomments', arm: 'invoice' },
  doNotBillReason: { column: 'donotbillreason', arm: 'invoice' },
  creditAmount: { column: 'creditamt', arm: 'invoice', numeric: true },
  creditReason: { column: 'creditreason', arm: 'invoice' },
  billToRefNo: { column: 'billtorefno', arm: 'invoice' },
  estInvoiceEmail: { column: 'estinvoiceemail', arm: 'invoice' },
  requireEdi: { column: 'requirededi', arm: 'invoice' },
  requireMrpl: { column: 'requiredmrpl', arm: 'invoice' },
  requirePod: { column: 'requiredpod', arm: 'invoice' },
  requireBol: { column: 'requiredbol', arm: 'invoice' },
  requireInInterchange: { column: 'requiredininterchange', arm: 'invoice' },
  requireOutInterchange: { column: 'requiredoutinterchange', arm: 'invoice' },
  requireChassisInEir: { column: 'requiredchassisineir', arm: 'invoice' },
  requireChassisOutEir: { column: 'requiredchassisouteir', arm: 'invoice' },
  requireRevisedDo: { column: 'requiredreviseddo', arm: 'invoice' },
  pendingApproval: { column: 'pendingapproval', arm: 'invoice' },
  // ordersall
  rebill: { column: 'rebill', arm: 'order' },
  cod: { column: 'cod', arm: 'order' },
  freightTerms: { column: 'freightterms', arm: 'order' },
  requirePerDiemApproval: { column: 'requireperdiemapproval', arm: 'order' },
  rateComments: { column: 'ratecomments', arm: 'order' },
  invoiceHoldReason: { column: 'invoiceholdreason', arm: 'order' },
  invoiceHoldFollowupDatetime: { column: 'invoiceholdfollowupdatetime', arm: 'order', datetime: true },
  billToContactRecno: { column: 'billtocontactrecno', arm: 'order', numeric: true, zeroDefault: true },
};

// Grouped stamp/link fields: ONE checkbox fans a PAIR of columns together
// (Available-to-Ship pattern) — the approval stamps + the quote link. Reading
// both halves from the anchor means an empty stamp clears the pair on the targets.
export const INVOICE_GROUPED_FIELDS: Record<string, { arm: InvArm; entries: Array<{ column: string; headerKey: string; numeric?: boolean; datetime?: boolean }> }> = {
  invoiceApproval: {
    arm: 'order',
    entries: [
      { column: 'approvedby', headerKey: 'invoiceApprovedBy' },
      { column: 'approveddatetime', headerKey: 'invoiceApprovedDatetime', datetime: true },
    ],
  },
  marginApproval: {
    arm: 'order',
    entries: [
      { column: 'marginapprovedby', headerKey: 'marginApprovedBy' },
      { column: 'marginapproveddatetime', headerKey: 'marginApprovedDatetime', datetime: true },
    ],
  },
  quoteLink: {
    arm: 'invoice',
    entries: [
      { column: 'quotenumber', headerKey: 'quoteNumber', numeric: true },
      { column: 'revisionnumber', headerKey: 'revisionNumber', numeric: true },
    ],
  },
};

/**
 * Which arm a flagged invoicing field writes through, or null for a key that fans
 * nothing (a checkbox drawn for a field no map names — there should be none).
 *
 * The arm is what decides whether the REGISTRY governs the field's checkbox. The order
 * arm writes ordersall columns, and `/change-all/preview` refuses any order column
 * `field_definitions.is_change_all` has not opened — refusing the WHOLE ChangeSet, not
 * the one column. So an order-arm checkbox drawn for an unflagged field does not fan
 * that field and quietly skip it; it takes every other flagged field on the pro down
 * with it. The invoice arm writes cms.invoice, which the registry does not cover (it is
 * scoped to orders/orderscurrent) and the endpoint does not enforce.
 */
export function invoicingFieldArm(field: string): InvArm | null {
  const simple = INVOICE_SIMPLE_FIELDS[field];
  if (simple) return simple.arm;
  const grouped = INVOICE_GROUPED_FIELDS[field];
  if (grouped) return grouped.arm;
  return null;
}

/**
 * The registry view_key(s) behind one checkbox.
 *
 * A simple field is its own key. A GROUPED one (`marginApproval`, `invoiceApproval`,
 * `quoteLink`) is a single checkbox over a PAIR of columns, and `field_definitions`
 * knows the halves — `marginApprovedBy` / `marginApprovedDatetime` — not the grouped
 * name. Asking the registry about the grouped name is how the approval checkboxes came
 * to read as permanently closed: no row could ever carry that key.
 *
 * Every half must be open. A pair is written together or not at all (an empty half
 * CLEARS its column on the targets), so a stamp whose name is fannable and whose date
 * is not would be a half-write no single-pro save can produce. Closing either half in
 * Data Management therefore closes the checkbox, which is the behavior an admin
 * reading that screen would expect.
 */
export function invoicingFieldViewKeys(field: string): string[] {
  const grouped = INVOICE_GROUPED_FIELDS[field];
  return grouped ? grouped.entries.map((e) => e.headerKey) : [field];
}

// Every header view key whose live value the fan-out needs — snapshotted at save time.
export const INVOICE_HEADER_VALUE_KEYS: string[] = [
  ...Object.keys(INVOICE_SIMPLE_FIELDS),
  ...Object.values(INVOICE_GROUPED_FIELDS).flatMap((g) => g.entries.map((e) => e.headerKey)),
];

const _toNum = (v: unknown): number | null => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
};

export function buildInvoicingChangeSet(
  header: Record<string, unknown>,
  checkedInv: ReadonlySet<string>,
): ChangeAllChangeSet {
  const invoice: ChangeAllChange[] = [];
  const order: ChangeAllChange[] = [];
  const bucket = (arm: InvArm) => (arm === 'invoice' ? invoice : order);

  for (const key of checkedInv) {
    const simple = INVOICE_SIMPLE_FIELDS[key];
    if (simple) {
      const raw = header[key];
      // Backend uppercases invoicecomments — send the raw FE string.
      const value = simple.numeric
        ? (_toNum(raw) ?? (simple.zeroDefault ? 0 : null))
        : simple.datetime
          ? toBackendDateValue(raw, true)
          : (raw ?? '');
      bucket(simple.arm).push({ column: simple.column, newValue: value });
      continue;
    }
    const grp = INVOICE_GROUPED_FIELDS[key];
    if (grp) {
      for (const e of grp.entries) {
        const raw = header[e.headerKey];
        const value = e.numeric
          ? (_toNum(raw) ?? 0)
          : e.datetime
            ? toBackendDateValue(raw, true)
            : (raw ?? '');
        bucket(grp.arm).push({ column: e.column, newValue: value });
      }
    }
  }
  const changes: ChangeAllChangeSet = {};
  if (invoice.length) changes.invoice = invoice;
  if (order.length) changes.order = order;
  return changes;
}

// ── Combined fan-out: the header/field arm and the line-item arm are applied in ONE
// Apply, but stay two endpoints/transactions (line items never run saverules, so they
// need not share the field arm's txn). These merge the two arms' per-PRO preview/apply
// results into the single shape the review modal renders. A PRO is eligible if EITHER
// arm can apply; an arm blocked on an otherwise-eligible PRO becomes a warning.
type ArmPreviewRow = { pronumber: number | string; eligible: boolean; warnings?: string[]; errors?: string[] };
type ArmApplyRow = { pronumber: number | string; applied: boolean; warnings?: string[]; errors?: string[] };

export function mergeInvoicingPreviewRows(
  pros: (number | string)[],
  headerRows: ArmPreviewRow[],
  lineRows: ArmPreviewRow[],
  hasHeader: boolean,
  hasLines: boolean,
): ChangeAllPreviewRow[] {
  const hBy = new Map(headerRows.map((r) => [String(r.pronumber), r]));
  const lBy = new Map(lineRows.map((r) => [String(r.pronumber), r]));
  return pros.map((p) => {
    const h = hBy.get(String(p));
    const l = lBy.get(String(p));
    const eligible = (hasHeader && !!h?.eligible) || (hasLines && !!l?.eligible);
    if (!eligible) {
      return {
        pronumber: p,
        eligible: false,
        errors: [...(hasHeader ? h?.errors ?? [] : []), ...(hasLines ? l?.errors ?? [] : [])],
      };
    }
    // Eligible overall — an arm's own notes/blockers become non-blocking warnings. (The
    // header arm reports an eligible PRO's arm-skip note in `errors`; the line arm splits
    // mismatch `warnings` from blocking `errors`.)
    const warnings = [
      ...(hasHeader && h ? h.errors ?? [] : []),
      ...(hasLines && l ? (l.eligible ? l.warnings ?? [] : l.errors ?? []) : []),
    ];
    return { pronumber: p, eligible: true, warnings, errors: [] };
  });
}

export function mergeInvoicingApplyResults(
  chunk: (number | string)[],
  headerResults: ArmApplyRow[],
  lineResults: ArmApplyRow[],
  hasHeader: boolean,
  hasLines: boolean,
): ChangeAllApplyResult[] {
  const hBy = new Map(headerResults.map((r) => [String(r.pronumber), r]));
  const lBy = new Map(lineResults.map((r) => [String(r.pronumber), r]));
  return chunk.map((p) => {
    const h = hBy.get(String(p));
    const l = lBy.get(String(p));
    const applied = (hasHeader && !!h?.applied) || (hasLines && !!l?.applied);
    return {
      pronumber: p,
      applied,
      errors: [
        ...(hasHeader && h && !h.applied ? h.errors ?? [] : []),
        ...(hasLines && l && !l.applied ? l.errors ?? [] : []),
      ],
      warnings: [
        ...(hasHeader && h ? h.warnings ?? [] : []),
        ...(hasLines && l ? l.warnings ?? [] : []),
      ],
    };
  });
}


/**
 * The saved (post-save) value behind each fannable header key. Extracted from
 * InvoicingView's `savedHeaderValue` so the fan-out can read the values from the details
 * payload the save just returned, with no mounted tab involved. Invoice-TABLE fields come
 * off the main invoice row; everything else is an ordersall column on the order itself.
 */
export function savedInvoiceHeaderValue(
  details: Partial<OrderDetails> | null,
  key: string,
): unknown {
  const d = (details ?? {}) as Record<string, unknown>;
  const inv = (details?.invoiceMain ?? null) as Record<string, unknown> | null;
  switch (key) {
    case 'invoiceComments': return inv?.invoiceComments ?? '';
    case 'doNotBillReason': return inv?.doNotBillReason ?? '';
    case 'billFromSystem': return inv?.billFromSystem ?? '';
    case 'creditAmount': return inv?.creditAmount != null ? String(inv.creditAmount) : '';
    case 'creditReason': return inv?.creditReason ?? '';
    case 'billToRefNo': return inv?.billToRefNo ?? '';
    case 'estInvoiceEmail': return inv?.estInvoiceEmail ?? '';
    // Linked quote — numbers, 0 = unlinked (V1's "no quote" sentinel).
    case 'quoteNumber': return inv?.quoteNumber ?? 0;
    case 'revisionNumber': return inv?.revisionNumber ?? 0;
    case 'requireEdi':
    case 'requireMrpl':
    case 'requirePod':
    case 'requireBol':
    case 'requireInInterchange':
    case 'requireOutInterchange':
    case 'requireChassisInEir':
    case 'requireChassisOutEir':
    case 'requireRevisedDo':
    case 'pendingApproval':
      return inv?.[key] ?? 'N';
    default:
      return d[key] ?? '';
  }
}

/** Every fannable header key's saved value, as buildInvoicingChangeSet wants it. */
export function invoicingHeaderValues(
  details: Partial<OrderDetails> | null,
): Record<string, unknown> {
  return Object.fromEntries(
    INVOICE_HEADER_VALUE_KEYS.map((k) => [k, savedInvoiceHeaderValue(details, k)]),
  );
}

/** What the container holds while a fan-out is pending review. ONE per Save. */
export interface ChangeAllFanout {
  pros: (number | string)[];
  /** order + invoice + legs (+ perDiemNotes / deleteReason) — a single ChangeSet, so the
   *  backend applies every arm to a pro in one transaction and one saverules run. */
  changes: ChangeAllChangeSet;
  /** Flagged invoice line items — a second endpoint inside the same Apply. */
  liLines: Array<Record<string, unknown>>;
}

/** The Invoicing tab's contribution, read from the POST-SAVE details. */
export interface InvoicingFanoutParts {
  changes: ChangeAllChangeSet;
  liLines: Array<Record<string, unknown>>;
}

/**
 * The Invoicing tab's flags as ChangeSet arms + line items. `details` must be the details
 * the save RETURNED: these values are read from the record, not from the edit buffer, so
 * a client-stamped field like the margin approval fans what actually persisted (and a
 * pre-save read would fan a blank).
 */
export function buildInvoicingFanoutParts(
  details: Partial<OrderDetails> | null,
  checkedFields: ReadonlySet<string>,
  checkedLines: ReadonlyMap<string, Record<string, unknown>>,
): InvoicingFanoutParts {
  const changes =
    checkedFields.size > 0
      ? buildInvoicingChangeSet(invoicingHeaderValues(details), checkedFields)
      : {};
  return { changes, liLines: Array.from(checkedLines.values()).filter((ln) => ln.item) };
}

/** Last-writer-wins per column, preserving first-seen order. */
function dedupeByColumn(rows: ChangeAllChange[]): ChangeAllChange[] {
  const byColumn = new Map<string, ChangeAllChange>();
  for (const r of rows) byColumn.set(String(r.column).toLowerCase(), r);
  return [...byColumn.values()];
}

/**
 * Fold the order/leg capture and the Invoicing tab's parts into ONE fan-out, or null when
 * there is nothing to fan (nothing flagged, or the anchor is the whole set).
 *
 * The order/leg rows are captured BEFORE the save (a successful save clears the edit
 * buffer) and the invoicing rows are read AFTER it, so they arrive separately and are
 * merged here. Where both name the same ordersall column the invoicing value wins: it was
 * read from the saved record, so it is what the anchor actually ended up with.
 */
export function mergeFanout(
  orderArm: { pros: (number | string)[]; changes: ChangeAllChangeSet } | null,
  invoicing: InvoicingFanoutParts | null,
  /** Used when only the invoicing arm has anything — the order arm carries its own. */
  invoicingPros: (number | string)[],
): ChangeAllFanout | null {
  const orderRows = [
    ...(orderArm?.changes.order ?? []),
    ...(invoicing?.changes.order ?? []),
  ];
  const invoiceRows = invoicing?.changes.invoice ?? [];
  const legRows = orderArm?.changes.legs ?? [];
  const liLines = invoicing?.liLines ?? [];

  const changes: ChangeAllChangeSet = {};
  if (orderRows.length) changes.order = dedupeByColumn(orderRows);
  if (invoiceRows.length) changes.invoice = dedupeByColumn(invoiceRows);
  if (legRows.length) changes.legs = legRows;
  if (orderArm?.changes.perDiemNotes !== undefined) {
    changes.perDiemNotes = orderArm.changes.perDiemNotes;
  }

  const pros = orderArm?.pros ?? invoicingPros;
  if (pros.length === 0) return null;
  const hasFields = !!(changes.order || changes.invoice || changes.legs);
  if (!hasFields && liLines.length === 0) return null;
  return { pros, changes, liLines };
}

/** The flagged line items, listed under the field chips so the Apply is reviewable. */
const LineItemSummary: React.FC<{ liLines: Array<Record<string, unknown>> }> = ({ liLines }) => (
  <div className="shrink-0">
    <div className="text-xs font-medium text-slate-600 dark:text-gray-300 mb-1">
      Invoice line items to apply (to every changed pro):
    </div>
    <div className="flex flex-col gap-0.5">
      {liLines.map((ln, i) => (
        <div key={i} className="text-xs text-slate-700 dark:text-gray-200">
          <span
            className={
              ln.isNew
                ? 'text-green-700 dark:text-green-400'
                : 'text-blue-700 dark:text-blue-400'
            }
          >
            {ln.isNew ? '+ new' : '~ edit'}
          </span>{' '}
          {String(ln.item)} — qty {String(ln.qty)} @ {String(ln.rate)} = {String(ln.total)}
        </div>
      ))}
    </div>
    <div className="mt-0.5 text-[0.6875rem] italic text-slate-500 dark:text-gray-400">
      New lines are added everywhere; edits match by line position, and a line whose
      position holds a different charge is skipped with a warning.
    </div>
  </div>
);

/**
 * The one review/results modal for a pending fan-out, whatever the user flagged and
 * wherever they flagged it. Rendered by whoever owns the pro (ProDetailContainer) so no
 * tab change can unmount it.
 *
 * The field arm and the line-item arm are two endpoints behind ONE Apply — the field arm
 * first, since saverules there can produce line items.
 */
export const ChangeAllFanoutModal: React.FC<{
  fanout: ChangeAllFanout;
  onClose: () => void;
  onApplied: () => void;
  /** The target set is templates, so the review copy says "templates" — the same
   *  modal, the same engine, a different record. */
  isTemplate?: boolean;
  /** The anchor, so the review step can reconcile its count with the banner's. */
  anchorPro?: number | string | null;
}> = ({ fanout, onClose, onApplied, isTemplate = false, anchorPro = null }) => {
  const { pros, changes, liLines } = fanout;
  const hasFields =
    (changes.order?.length ?? 0) > 0
    || (changes.invoice?.length ?? 0) > 0
    || (changes.legs?.length ?? 0) > 0;
  const hasLines = liLines.length > 0;
  // A bulk delete gets its own title — the review step should say what it is.
  const isDelete = (changes.order ?? []).some(
    (c) => c.column === 'deleted' && String(c.newValue).toUpperCase() === 'Y',
  );
  return (
    <ChangeAllReviewModal
      isOpen
      pros={pros}
      changes={changes}
      title={isDelete ? 'Delete All' : 'Change All'}
      recordNoun={isTemplate ? 'template' : 'pro'}
      anchorPro={anchorPro}
      extraSummary={hasLines ? <LineItemSummary liLines={liLines} /> : null}
      previewFn={async (p, signal) => {
        const [fRes, lRes] = await Promise.all([
          hasFields
            ? ordersService.changeAllPreview({ pros: p, changes }, signal)
            : Promise.resolve({ rows: [] }),
          hasLines
            ? invoiceLineItemsService.changeAllPreview(p, liLines, signal)
            : Promise.resolve({ rows: [] }),
        ]);
        return { rows: mergeInvoicingPreviewRows(p, fRes.rows, lRes.rows, hasFields, hasLines) };
      }}
      applyFn={async (chunk, nonce) => {
        // Field arm FIRST (saverules, incl. any line-item effects), THEN the line-item
        // arm — legacy order. Same nonce; the apply log keys on (nonce, pro, kind).
        const fRes = hasFields
          ? await ordersService.changeAllApply({ pros: chunk, changes, submitNonce: nonce })
          : { results: [] };
        const lRes = hasLines
          ? await invoiceLineItemsService.changeAllApply(chunk, liLines, nonce)
          : { results: [] };
        return {
          results: mergeInvoicingApplyResults(chunk, fRes.results, lRes.results, hasFields, hasLines),
        };
      }}
      onClose={onClose}
      onApplied={onApplied}
    />
  );
};
